import { useMemo } from "react";
import { usePaymentMethods } from "@/hooks/usePaymentMethods";
import { usePermissions } from "@/hooks/usePermissions";
import type { PaymentMethod } from "@/interfaces/paymentMethod";

/**
 * The card the campaign-builder checkout bills against. Cards are stored per group, so this
 * resolves a group and picks its default card (falling back to the first one, matching how the
 * group billing surfaces read the list).
 *
 * `walletGroupId` is the wallet a multi-group client (Pro Plus or Enterprise) chose. Without one —
 * every other plan, where a client operates as a single group — it falls back to the caller's own
 * placement, so the behaviour is unchanged for them. The card follows the SAME group as the
 * credits: a campaign funded from one wallet and charged to another group's card is the kind of
 * split nobody notices until the invoice.
 *
 * Adding a card goes through the same SetupIntent flow the group admin surface uses, which is
 * gated on `buy CREDIT` on the API (`scopeWriteGuard("CREDIT", "buy")`) — `canAddCard` mirrors
 * that so the UI doesn't offer a button that would 403.
 */
export function useCampaignPaymentMethod(walletGroupId?: number | null): {
  card: PaymentMethod | null;
  groupId: number | null;
  canAddCard: boolean;
  isLoading: boolean;
} {
  const { groupId: placement, can, isLoaded } = usePermissions();
  const groupId = walletGroupId ?? placement;
  const { data, isLoading } = usePaymentMethods(groupId);

  const card = useMemo(() => data?.find((method) => method.isDefault) ?? data?.[0] ?? null, [data]);

  return {
    card,
    groupId,
    canAddCard: can("buy", "CREDIT"),
    isLoading: !isLoaded || (groupId != null && isLoading),
  };
}
